import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";

interface ContactFormProps {
  showCategorySelection?: boolean;
}

/**
 * Minimal, mobile-first contact form.
 *
 * Reduced to the fields that actually gate a reply — name, email, an optional
 * interest category and the message. Everything else (training frequency,
 * language, goals) is collected in the follow-up conversation, which keeps the
 * mobile conversion path to a single screen.
 */
export const ContactForm = ({ showCategorySelection = false }: ContactFormProps) => {
  const { toast } = useToast();
  const { t, i18n } = useTranslation();

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const messageValue = (formData.get("message") as string) || "";

    const urlPattern = /(https?:\/\/[^\s]+)|(www\.[^\s]+)|([a-zA-Z0-9]+-?[a-zA-Z0-9]+\.(com|org|net|edu|gov|co\.[a-z]{2}))/gi;

    if (urlPattern.test(messageValue)) {
      toast({
        title: t("contactForm.error"),
        description: t("contactForm.noLinksAllowed"),
        variant: "destructive",
      });
      return;
    }

    const name = (formData.get("name") as string) || "";
    const email = (formData.get("email") as string) || "";
    const category = (formData.get("category") as string) || "";

    const lines = [
      `Name: ${name}`,
      `Email: ${email}`,
      category ? `Category: ${category}` : "",
      `Language: ${i18n.language}`,
      `\nMessage:\n${messageValue}`,
    ].filter(Boolean);

    const mailtoLink = `mailto:support@valorform.com?subject=${encodeURIComponent(
      `Contact request — ${name}`,
    )}&body=${encodeURIComponent(lines.join("\n"))}`;
    window.location.href = mailtoLink;

    toast({
      title: t("contactForm.formSubmitted"),
      description: t("contactForm.emailClientOpen"),
    });
    form.reset();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div>
        <label htmlFor="cf-name" className="block text-sm font-medium text-foreground mb-2">{t("contactForm.name")} *</label>
        <Input id="cf-name" name="name" required maxLength={100} autoComplete="name" className="bg-background border-border" />
      </div>

      <div>
        <label htmlFor="cf-email" className="block text-sm font-medium text-foreground mb-2">{t("contactForm.email")} *</label>
        <Input id="cf-email" name="email" type="email" required maxLength={255} autoComplete="email" inputMode="email" className="bg-background border-border" />
      </div>

      {showCategorySelection && (
        <div>
          <label htmlFor="cf-category" className="block text-sm font-medium text-foreground mb-2">{t("contactForm.interestedIn")} *</label>
          <Select name="category" required>
            <SelectTrigger id="cf-category" className="bg-background border-border">
              <SelectValue placeholder={t("contactForm.selectCategory")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="football">{t("contactForm.football")}</SelectItem>
              <SelectItem value="fitness">{t("contactForm.fitness")}</SelectItem>
              <SelectItem value="elite">{t("contactForm.elite")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      <div>
        <label htmlFor="cf-message" className="block text-sm font-medium text-foreground mb-2">{t("contactForm.message")} *</label>
        <Textarea
          id="cf-message"
          name="message"
          required
          maxLength={2000}
          className="bg-background border-border min-h-28"
          placeholder={t("contactForm.messagePlaceholder")}
        />
      </div>

      <p className="text-xs text-muted-foreground">{t("contactForm.step1Hint")}</p>

      <Button type="submit" className="w-full shadow-accent-glow" size="lg">
        {t("contactForm.submit")}
      </Button>
    </form>
  );
};
